import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Award } from 'lucide-react';
import { getExcellence, createExcellence, updateExcellence, deleteExcellence } from '../services/adminApi';
import FileUpload from '../components/FileUpload';

const ExcellenceManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    image: '',
    description: '',
    order: 0
  });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const data = await getExcellence();
      setItems(data);
    } catch (error) {
      console.error('Failed to fetch excellence items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await updateExcellence(editingItem._id, formData);
      } else {
        await createExcellence(formData);
      }
      fetchItems();
      resetForm();
    } catch (error) {
      console.error('Failed to save excellence item:', error);
      alert('Failed to save excellence item: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      image: item.image,
      description: item.description || '',
      order: item.order || 0
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this excellence item?')) {
      try {
        await deleteExcellence(id);
        fetchItems();
      } catch (error) {
        console.error('Failed to delete excellence item:', error);
        alert('Failed to delete excellence item');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      image: '',
      description: '',
      order: 0
    });
    setEditingItem(null);
    setShowModal(false);
  };

  if (loading) {
    return <div className="text-white text-center py-12">Loading excellence items...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-black text-white mb-2">Excellence Manager</h1>
          <p className="text-gray-400">Manage "Our Excellence" section images and content</p>
        </div>
        <button onClick={() => setShowModal(true)} className="flex items-center space-x-2 bg-yellow-400 hover:bg-yellow-300 text-black font-bold px-6 py-3 rounded-lg transition-colors">
          <Plus size={20} />
          <span>New Item</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((item) => (
          <div key={item._id} className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden hover:border-yellow-400 transition-colors">
            <img src={item.image} alt={item.title} className="w-full h-48 object-cover" />
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
              {item.description && <p className="text-gray-400 text-sm mb-4">{item.description}</p>}
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Order: {item.order}</span>
                <div className="flex space-x-2">
                  <button onClick={() => handleEdit(item)} className="p-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-blue-400 transition-colors">
                    <Edit2 size={16} />
                  </button>
                  <button onClick={() => handleDelete(item._id)} className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {items.length === 0 && (
        <div className="text-center py-12">
          <Award className="mx-auto mb-4 text-gray-600" size={48} />
          <p className="text-gray-400">No excellence items found</p>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col my-8">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">{editingItem ? 'Edit Excellence Item' : 'Add New Excellence Item'}</h2>
              <button
                type="button"
                onClick={resetForm}
                className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="text-white font-medium mb-2 block">Title *</label>
                <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" placeholder="Excellence title" />
              </div>
              <FileUpload
                value={formData.image}
                onChange={(url) => setFormData({ ...formData, image: url })}
                type="images"
                accept="image/*"
                label="Excellence Image *"
              />
              <div>
                <label className="text-white font-medium mb-2 block">Description</label>
                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" placeholder="Brief description" />
              </div>
              <div>
                <label className="text-white font-medium mb-2 block">Display Order</label>
                <input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })} className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400" />
              </div>
              <div className="flex justify-end space-x-4 pt-4">
                <button type="button" onClick={resetForm} className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors">Cancel</button>
                <button type="submit" className="px-6 py-3 bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-lg transition-colors">{editingItem ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExcellenceManager;